import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const InstanceContext = createContext(null);

export function InstanceProvider({ children }) {
  const { user, token } = useAuth();
  const [instances, setInstances] = useState([]);
  const [currentInstance, setCurrentInstance] = useState(null);
  const [instanceSettings, setInstanceSettings] = useState({
    currency: 'BRL',
    currency_symbol: 'R$',
    decimal_places: 2,
    language: 'pt-BR',
    date_format: 'DD/MM/YYYY',
  });
  const [loading, setLoading] = useState(false);

  // Check if user is admin-master
  const isAdminMaster = user?.role === 'admin-master';

  // Load instances for admin-master
  useEffect(() => {
    if (isAdminMaster && token) {
      loadInstances();
    }
  }, [isAdminMaster, token]);

  // Load current instance settings
  useEffect(() => {
    if (user?.instance_id && user?.instance_id !== 'master') {
      loadInstanceSettings(user.instance_id);
    }
  }, [user?.instance_id]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const res = await api.get('/instances');
      setInstances(res.data);
      
      // Set first instance as default if none selected
      if (res.data.length > 0 && !currentInstance) {
        const savedInstanceId = localStorage.getItem('selectedInstanceId');
        const savedInstance = res.data.find(i => i.id === savedInstanceId);
        if (savedInstance) {
          setCurrentInstance(savedInstance);
          setInstanceSettings(savedInstance.settings || instanceSettings);
        }
      }
    } catch (err) {
      console.error('Failed to load instances:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInstanceSettings = async (instanceId) => {
    try {
      const res = await api.get(`/instances/${instanceId}`);
      if (res.data?.settings) {
        setInstanceSettings(res.data.settings);
        setCurrentInstance(res.data);
      }
    } catch (err) {
      // Instance might not exist or user doesn't have access
      console.log('Could not load instance settings');
    }
  };

  const switchInstance = (instance) => {
    setCurrentInstance(instance);
    if (instance?.settings) {
      setInstanceSettings(instance.settings);
    }
    localStorage.setItem('selectedInstanceId', instance?.id || '');
  };

  // Currency formatting based on instance settings
  const formatCurrency = (value) => {
    const numValue = Number(value) || 0;
    return new Intl.NumberFormat(instanceSettings.language === 'en' ? 'en-US' : 'pt-BR', {
      style: 'currency',
      currency: instanceSettings.currency || 'BRL',
      minimumFractionDigits: instanceSettings.decimal_places || 2,
      maximumFractionDigits: instanceSettings.decimal_places || 2,
    }).format(numValue);
  };

  // Format number with decimal places
  const formatNumber = (value, decimals = null) => {
    const numValue = Number(value) || 0;
    const decimalPlaces = decimals ?? instanceSettings.decimal_places ?? 2;
    return new Intl.NumberFormat(instanceSettings.language === 'en' ? 'en-US' : 'pt-BR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numValue);
  };

  // Format date based on instance settings
  const formatDate = (date, includeTime = false) => {
    if (!date) return '-';
    const d = new Date(date);
    const locale = instanceSettings.language === 'en' ? 'en-US' : 'pt-BR';
    
    if (includeTime) {
      return d.toLocaleString(locale);
    }
    return d.toLocaleDateString(locale);
  };

  const value = {
    instances,
    currentInstance,
    instanceSettings,
    isAdminMaster,
    loading,
    switchInstance,
    loadInstances,
    formatCurrency,
    formatNumber,
    formatDate,
  };

  return (
    <InstanceContext.Provider value={value}>
      {children}
    </InstanceContext.Provider>
  );
}

export function useInstance() {
  const context = useContext(InstanceContext);
  if (!context) {
    throw new Error('useInstance must be used within an InstanceProvider');
  }
  return context;
}

export default InstanceContext;
